import type { GroupReportMetricsInterface, NewStudentTutorDataMetricsInterface } from '@whizz/api-model';
import type { ClassReportStudentType, ClassReportType } from '../pages/types';
import type { TableReportType, TableStudentType, TopicChartElementType, TopicChartType } from './groupTopicReportMapper';
import type {
  TopicProgressStudentInterface,
} from '@whizz/api-model/dist/@types/interface/tx3/GroupReportMetricsInterface';
import { round } from '../util/numbers';
import { convertYearsToWeeks, getDiffWeeks } from '../util/dates';
import { ConsistencyEnum } from '@whizz/api-model';
import { repository } from '@whizz/api-client';

export const initialValueGroupReportMetrics = {
  classReport: {
    students: {
      all: [],
      unassessedOrReassessedStudents: [],
      assessedNotBeingReassessedStudents: [],
    },
    mathsAgeAverage: 0,
    mathsAgeImprovementAverage: 0,
    progressionAverage: 0,
    qualityLearningTimeAverage: 0,
    consistencyAverage: 0,
    consistencyAverageName: ConsistencyEnum.OCCASIONAL,
    tickStatusAverage: 'green',
    studentStatusAverage: 'superstar',
    timeSpanAverage: 0,
    mathsAgeAtStartAverage: 0,
    tutorUsageAverage: 0,
    topicChallengeUsageAverage: 0,
    progressionPassRateAverage: 0,
    exerciseMarkAverage: 0,
    testMarkAverage: 0,
    actualAgeAverage: 0,
    minutesPerProgressionAverage: 0,
  },
  chartTopic: [],
  tableTopic: {
    mathsAgeAvg: 0,
    topics: [],
    students: [],
  },
};

export const mapAllGroupReportForClassReport = (
  allReport: GroupReportMetricsInterface | null,
  assessedNotBeingReassessedReport: GroupReportMetricsInterface | null,
  unassessedOrReassessedReport: GroupReportMetricsInterface | null,
): {
  classReport: ClassReportType
  chartTopic: TopicChartType[]
  tableTopic: TableReportType
} => {
  if (
    (allReport === null)
    || (assessedNotBeingReassessedReport === null)
    || (unassessedOrReassessedReport === null)
  ) return initialValueGroupReportMetrics;

  const classReport = mapClassReport(allReport, assessedNotBeingReassessedReport, unassessedOrReassessedReport);
  // TODO: Check if all students for topic charts
  const mappedStudentsDataForTopicProgress = getMappedStudentsDataForTopicProgress(allReport);
  const chartTopic = mapChartTopic(allReport, mappedStudentsDataForTopicProgress);
  const tableTopic = mapTableTopic(allReport, classReport, mappedStudentsDataForTopicProgress);

  return {
    classReport,
    chartTopic: chartTopic.filter((topic) => topic.endAvgTopicAge !== 0),
    tableTopic,
  };
};

const mapClassReport = (
  allReport: GroupReportMetricsInterface,
  assessedNotBeingReassessedReport: GroupReportMetricsInterface,
  unassessedOrReassessedReport: GroupReportMetricsInterface,
): ClassReportType => {
  const mapStudents = (groupReportMetricsApi: GroupReportMetricsInterface): ClassReportStudentType[] => {
    const weeks = getDiffWeeks(groupReportMetricsApi.startDate, groupReportMetricsApi.endDate);
    return groupReportMetricsApi.studentsMetrics.map((st) => mapClassReportStudent(st, weeks));
  };

  const students = {
    all: mapStudents(allReport),
    unassessedOrReassessedStudents: mapStudents(unassessedOrReassessedReport),
    assessedNotBeingReassessedStudents: mapStudents(assessedNotBeingReassessedReport),
  };

  const { average } = allReport;
  const diffWeeksAv = getDiffWeeks(allReport.startDate, allReport.endDate);

  return {
    students,

    // Main metrics
    mathsAgeAverage: average.mathsAge,
    mathsAgeImprovementAverage: convertYearsToWeeks(average.mathsAgeImprovement),
    progressionAverage: average.progressionsPerWeek,
    qualityLearningTimeAverage: average.learningTime,
    consistencyAverage: average.consistency,
    consistencyAverageName: repository.GroupReportMetricsRepository.getConsistencyName(average.consistency, diffWeeksAv),
    tickStatusAverage: average.tickStatus,
    studentStatusAverage: average.studentStatus,

    // Additional metrics
    timeSpanAverage: average.timeSpan,
    mathsAgeAtStartAverage: average.mathsAgeAtStart,
    tutorUsageAverage: average.tutorUsage,
    topicChallengeUsageAverage: average.topicChallengeUsage,
    progressionPassRateAverage: average.progressionPassRate,
    exerciseMarkAverage: average.exerciseMark,
    testMarkAverage: average.testMark,
    actualAgeAverage: average.actualAge,
    minutesPerProgressionAverage: average.minutesPerProgression,
  };
};

const mapClassReportStudent = (student: NewStudentTutorDataMetricsInterface, diffWeeks: number): ClassReportStudentType => {
  if (diffWeeks < 1) {
    diffWeeks = 1;
  }
  return {
    id: student.id,
    childId: student.childId,
    fullName: (student.lastName) ? `${student.firstName} ${student.lastName}` : student.firstName,
    firstName: student.firstName,
    gender: student.gender,
    selected: false,

    // Main metrics
    consistency: student.consistency,
    consistencyName: repository.GroupReportMetricsRepository.getConsistencyName(student.consistency, diffWeeks),
    tickStatus: student.tickStatus,
    studentStatus: student.studentStatus,
    progressions: student.progressions,

    chartsData: {
      mathsAge: student.mathsAge,
      mathsAgeImprovement: convertYearsToWeeks(student.mathsAgeImprovement),
      progression: student.progressionsPerWeek,
      qualityLearningTime: student.learningTime,
    },

    // Additional metrics
    lastAssessmentDate: student.lastAssessmentDate,
    timeSpan: student.timeSpan,
    mathsAgeAtStart: student.mathsAgeAtStart,
    tutorUsage: student.tutorUsage,
    topicChallengeUsage: student.topicChallengeUsage,
    topicChallengesPassed: {
      count: student.topicChallengesPassed,
      total: student.totalTopicChallenges,
    },
    progressionPassRate: student.progressionPassRate,
    exerciseMark: student.exerciseMark,
    testMark: student.testMark,
    actualAge: student.actualAge,
    minutesPerProgression: student.minutesPerProgression,
    parentLink: student.hasParentLink,
    tags: student.tags,
  };
};

type MappedStudentsDataForTopicProgressType = {
  [id: number]: {
    gender: number
    mathsAge: number
  }
};
const getMappedStudentsDataForTopicProgress = (groupReportMetrics: GroupReportMetricsInterface): MappedStudentsDataForTopicProgressType => {
  const data: MappedStudentsDataForTopicProgressType = {};
  groupReportMetrics.studentsMetrics.forEach((st) => {
    data[st.id] = {
      gender: st.gender || 0,
      mathsAge: st.mathsAge,
    };
  });
  return data;
};

const mapChartTopic = (
  groupReportMetrics: GroupReportMetricsInterface,
  mappedStudentsDataForTopicProgress: MappedStudentsDataForTopicProgressType,
): TopicChartType[] => {
  return groupReportMetrics.averageTopicProgress.map((topicProgressAvg) => ({
    index: topicProgressAvg.topic.index,
    name: topicProgressAvg.topic.name,
    startAvgTopicAge: topicProgressAvg.startValue,
    endAvgTopicAge: topicProgressAvg.endValue,
    elements: groupReportMetrics.studentsTopicProgress
      .reduce((acc, current) => {
        const topicProgress = current.topicProgress.find((topicProgress) => topicProgress.topic.index === topicProgressAvg.topic.index);

        if (null == topicProgress) {
          return acc;
        }

        return [
          ...acc,
          {
            id: current.id,
            fullName: current.name,
            gender: mappedStudentsDataForTopicProgress[current.id].gender,
            startTopicAge: topicProgress.startValue,
            progressTopicAge: topicProgress.progressValue,
            endTopicAge: topicProgress.endValue,
          },
        ];
      }, [] as TopicChartElementType[]),
  }));
};

const mapTableTopic = (
  groupReportMetrics: GroupReportMetricsInterface,
  classReport: ClassReportType,
  mappedStudentsDataForTopicProgress: MappedStudentsDataForTopicProgressType,
): TableReportType => {
  return {
    mathsAgeAvg: round(classReport.mathsAgeAverage, 2),
    topics: groupReportMetrics.averageTopicProgress.map((avrTP) => ({
      index: avrTP.topic.index,
      name: avrTP.topic.name,
      topicAgeAvg: round(avrTP.endValue, 2),
    })),
    students:groupReportMetrics.studentsTopicProgress.map((st) => mapTableTopicProgress(
      st,
      mappedStudentsDataForTopicProgress,
    )),
  };
};

const mapTableTopicProgress = (
  studentTP: TopicProgressStudentInterface,
  mappedStudentsDataForTopicProgress: MappedStudentsDataForTopicProgressType,
): TableStudentType => {
  const tableStudent: TableStudentType = {
    id: studentTP.id,
    name: studentTP.name,
    mathsAge: round(mappedStudentsDataForTopicProgress[studentTP.id].mathsAge, 2),
  };

  studentTP.topicProgress.forEach((tp) => {
    const topicProperty = `topic${tp.topic.index}`;
    tableStudent[topicProperty] = tp.endValue;
  });

  return tableStudent;
};

